import * as React from 'react';
import { useState } from 'react';

import { ChangeShippingOptionModal } from './ChangeShippingOptionModal';
import { ModificationOrderModal } from './ModificationOrderModal';
import { Pencil } from '../assets/Pencil';
import { isAdmin, isOpsAdmin, isAllModuleAccessUser, isTeamMember } from '../pages/constants';
import { Customer, Order, OrderStatus } from '../pages/types';
import {
  formatCountryCode,
  formatDeliveryDateEstimates,
  getModificationOrderEligibility,
  SHIPMENT_DISPLAY_VALUE,
} from '../utils';
import { isCanadianCustomer } from '../utils/country';

interface ShippingDetailProps {
  customer: Customer;
  refreshCustomer: () => void;
  order: Order;
}

const getCarrierRateClass = (order: Order) => {
  const crc = [...new Set(order.fulfillments.map((f) => f.carrier_rate_class ?? '--'))];
  return crc.join(', ');
};

export const ShippingDetail = ({ customer, refreshCustomer, order }: ShippingDetailProps) => {
  let fullAddress = '--';
  const [showChangeShippingOptionModal, setShowChangeShippingOptionModal] = useState(false);
  const [showModificationOrderModal, setShowModificationOrderModal] = useState(false);

  if (order.fulfillments.length > 0) {
    const { address1, address2, city, state, zip, country } = order.fulfillments[0];
    const formattedCountry = formatCountryCode(country);
    fullAddress = [address1, address2, city, state, zip, formattedCountry]
      .filter(Boolean)
      .join(', ');
  }
  const showEditShippingCTA =
    !isCanadianCustomer(customer) &&
    order.status === OrderStatus.PREVIEW &&
    order.products.every((product) => !product.is_frozen) &&
    !!(isAdmin() || isOpsAdmin() || isAllModuleAccessUser() || isTeamMember());
  const isEligibleForModificationOrder =
    getModificationOrderEligibility(order) && isAllModuleAccessUser();

  return (
    <div className="pb-3">
      <div className="mb-1 flex items-center">
        <h2 className="inline-block font-bold">Shipping Details</h2>
        {isEligibleForModificationOrder && (
          <div className="text-sm ml-2">
            <p
              className="cursor-pointer underline text-anchor"
              onClick={() => setShowModificationOrderModal(true)}
            >
              Send to a Different Address
            </p>
          </div>
        )}
      </div>
      <div className="bg-white border rounded-lg p-4 shadow-md w-full min-h-[262px]">
        <div className="mb-2">
          <strong>Shipping Address:</strong> {fullAddress}
        </div>
        <div className="mb-2">
          <strong>Shipping Method: </strong>
          {SHIPMENT_DISPLAY_VALUE[order?.shipping_method ?? ''] ?? '--'}
          {showEditShippingCTA && (
            <Pencil
              className="inline w-4 h-4 mx-1 cursor-pointer"
              onClick={() => setShowChangeShippingOptionModal(true)}
            />
          )}
        </div>
        <div className="mb-2">
          <strong>Carrier Rate Class:</strong> {getCarrierRateClass(order)}
        </div>
        <div className="mb-2">
          <strong>Estimated Delivery Date:</strong> {formatDeliveryDateEstimates(order)}
        </div>
        <div className="mb-2">
          <strong>Shipping Price:</strong>{' '}
          {order.total_shipping_price ? `$${order.total_shipping_price}` : '$0.00'} {order.currency}
        </div>
      </div>
      {showChangeShippingOptionModal && (
        <ChangeShippingOptionModal
          customer={customer}
          order={order}
          refreshCustomer={refreshCustomer}
          onConfirm={() => {
            setShowChangeShippingOptionModal(false);
          }}
          onCancel={() => setShowChangeShippingOptionModal(false)}
        />
      )}
      {showModificationOrderModal && (
        <ModificationOrderModal
          customer={customer}
          order={order}
          onConfirm={() => setShowModificationOrderModal(false)}
          onCancel={() => setShowModificationOrderModal(false)}
        />
      )}
    </div>
  );
};
